import "./SearchPatientForm.scss";
import { Button } from "antd";
import { RecentVisits } from "./RecentVisits";
import { SearchPatientField } from "./SearchPatientField/SearchPatientField";
import { VyneForm } from "@vyne-shared/components/vyneForm/VyneForm";
import { useMasonFlag } from "utils/useMasonFlag";
import { useState } from "react";
import type { ExistingPatientResult } from "api/responses";

/** Defines properties for the form used to search for an existing Patient/Responsible Party. */
interface SearchPatientFormProps {
    /** Callback to execute when an existing P/RP has been selected. */
    readonly onSelectExistingPrp: (prp: ExistingPatientResult) => void;

    /** Callback to execute if the user has chosen to create a new P/RP.
     * If null, the P/RP creation feature is not rendered. */
    readonly onSelectNewPrp: (() => void) | null;

    /** If set, in Mason-integrated sessions, P/RPs not linked to PMS patients are excluded. */
    readonly excludeUnlinked?: boolean;

    /** If set, in Mason-integrated sessions, recent patient appointments are shown. */
    readonly showRecentVisits?: boolean;
}

/** Form used to search for an existing Patient/Responsible Party to update, or elect to create a new P/RP. */
export const SearchPatientForm = ({
    onSelectExistingPrp,
    onSelectNewPrp,
    excludeUnlinked,
    showRecentVisits,
}: SearchPatientFormProps) => {
    const [searchFieldFocused, setSearchFieldFocused] = useState(false);

    const masonIntegratedSession = useMasonFlag();
    showRecentVisits &&= masonIntegratedSession;

    const showNew = !masonIntegratedSession && onSelectNewPrp;

    return (
        <VyneForm className="vyne-search-patient">
            <SearchPatientField
                onSelectExisting={onSelectExistingPrp}
                excludeUnlinked={excludeUnlinked}
                active={searchFieldFocused}
                onFocus={() => setSearchFieldFocused(true)}
            />
            {showRecentVisits ? (
                <RecentVisits
                    expanded={!searchFieldFocused}
                    onHeaderClick={() => setSearchFieldFocused((x) => !x)}
                    onSelectExistingPrp={onSelectExistingPrp}
                />
            ) : null}
            {/* Add spacing so "Add New Patient" is not obscured by "No Results" menu item */}
            {showNew && (
                <p className="vyne-search-patient-add-new">
                    Not an existing patient?
                    <Button type="link" onClick={onSelectNewPrp}>
                        Add New Patient
                    </Button>
                </p>
            )}
        </VyneForm>
    );
};
